import {
  Bars3BottomLeftIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import { getRootPersonaFromLexicon } from '@rabbit/bizproc/client';
import { nestApiCreateNote, nestApiUpdateNote, nestApiDeleteNote } from '@rabbit/bizproc/core';
import { useGetNotes } from '@rabbit/bizproc/react';
import { useIdentity } from '@rabbit/data/portal';
import {
  DTNote,
  NoteCategoryType,
  NoteEntityType,
  NoteType,
  PersonaTypeSingleLetter,
  PrincipalsFieldName,
} from '@rabbit/data/types';
import {
  Button,
  ButtonIcon,
  formatUnixTime,
  Input,
  LabelledItemList,
  LabelledItemShape,
  Modal,
} from '@rabbit/elements/shared-components';
import { formatDate, titleCase } from '@rabbit/sage/utils/helpers';
import { Formik, Form } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as Yup from 'yup';


const validationSchema = Yup.object().shape({
  title: Yup.string().trim().required('Please enter a title'),
  description: Yup.string().trim().required('Please enter a description'),
  category: Yup.string().required('Please select a category'),
});

interface NoteShape {
  title: string;
  description: string;
  category: string;
  date: number;
}
type NoteMode = 'add' | 'edit' | 'view';

export function Notes({
  entityId,
  entityType,
}: {
  entityId: string;
  entityType: NoteEntityType;
}) {
  const { t } = useTranslation();
  const activePremiumTenant = t('tenantLink');
  const {
    data: { identity },
  } = useIdentity();
  const { notes, deleteNote } =
    useGetNotes({
      tenantLink: activePremiumTenant,
      entityId,
      entityType,
    }) ?? {};
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<NoteMode>('view');
  const [editNote, setEditNote] = useState<DTNote | null>(null);
  const [viewNote, setViewNote] = useState<DTNote | null>(null);

  const categoryColors: Record<string, string> = {
    complaint: 'bg-red-100 text-red-800',
    support: 'bg-blue-100 text-blue-800',
    feedback: 'bg-green-100 text-green-800',
    sales: 'bg-purple-100 text-purple-800',
    technical: 'bg-yellow-100 text-yellow-800',
  };
  
  const defaultCategoryStyle = 'bg-gray-100 text-gray-800';

  const categoryOptions = Object.values(NoteCategoryType).map((value) => ({
    label: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase(),
    value: value,
  }));

  const onSubmit = async (values: NoteShape) => {
    try {
      setSubmitting(true);

      const warrantor = getRootPersonaFromLexicon(
        t(PrincipalsFieldName),
        PersonaTypeSingleLetter.Warrantor
      );

      const noteData = {
        title: values.title,
        content: values.description,
        interactionDate: new Date(values.date).getTime(),
        category: values.category as any,
        noteType: NoteType.GENERAL,
        entityType: entityType,
        entityId: entityId,
        tenantLink: activePremiumTenant,
        warrantor: warrantor,
        updatedBy: identity.docid,
        updateByName: identity.displayname,
      };

      if (mode === 'edit' && editNote) {
        const response = await nestApiUpdateNote({
          docid: editNote.docid,
          ...noteData,
        });
        if (response) {
          toast.success(t('Note updated successfully!'));
        } else {
          toast.error(t('Unable to save note'));
        }
      } else {
        const res = await nestApiCreateNote(noteData);
        if (res) {
          toast.success(t('Note created successfully!'));
        } else {
          toast.error(t('Unable to save note'));
        }
      }
      setMode('view');
    } catch (error) {
      console.error('NotesDetailContentSection: Error saving note:', error);
      toast.error(t('message.somethingWentWrongPleaseTryAgain'));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteNoteItem = async (note: DTNote) => {
    try {
      await nestApiDeleteNote({ docid: note.docid });
      toast.success(t('Note deleted successfully!'));
    } catch (error) {
      toast.error(t('Unable to delete note'));
    }
  };

  return (
    <>
      <div className="relative">
        <ButtonIcon
          kind="bgLightGreen"
          Icon={Bars3BottomLeftIcon}
          onClick={() => setShowModal(true)}
          label={t('general.notes')}
          iconLeft
          count={notes.length}
        />
      </div>
      {showModal && (
        <>
          <Modal
            kind="generic"
            settings={{
              title: t('general.notes'),
              handleClose: () => setShowModal(false),
              loading: loading,
            }}
            className="w-full md:w-[640px]"
          >
            {(mode === 'add' || mode === 'edit') && (
              <Formik
                initialValues={{
                  title: mode === 'edit' && editNote ? editNote.title : '',
                  description:
                    mode === 'edit' && editNote ? editNote.content : '',
                  category:
                    mode === 'edit' && editNote ? editNote.category : '',
                  date: mode === 'edit' && editNote ? editNote.interactionDate : Date.now(),
                }}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                validateOnChange={false}
                validateOnBlur={false}
              >
                {({ errors, values }) => (
                  <Form className="flex flex-col gap-4 px-5 py-4">
                    <Input
                      type="datepicker"
                      name="date"
                      label={t('Date')}
                      settings={{
                        placeholder: 'DD/MM/YYYY',
                      }}
                    />
                    <Input
                      type="text"
                      name="title"
                      label={t('Title') + '*'}
                      settings={{ placeholder: t('Enter note title') }}
                    />
                    <Input
                      type="select"
                      name="category"
                      label={t('Category') + '*'}
                      settings={{
                        options: categoryOptions,
                      }}
                    />
                    <Input
                      type="rich-text"
                      name="description"
                      label={t('Description') + '*'}
                      settings={{}}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Button kind="primary" type="submit" loading={submitting}>
                        {mode === 'edit' ? t('Save note') : t('Add note')}
                      </Button>
                      <Button
                        kind="outline_red"
                        type="button"
                        onClick={() => setMode('view')}
                      >
                        {t('Cancel')}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
            {mode === 'view' && (
              <>
                <div className="mt-4 flex justify-end px-4">
                  <Button
                    icon={<PlusIcon />}
                    kind="outline"
                    size={'sm'}
                    onClick={() => setMode('add')}
                  >
                    {t('Add note')}
                  </Button>
                </div>
                <div className="flex flex-col gap-4 p-4">
                  {notes.map((note: any) => (
                    <NoteDetail
                      key={note.docid}
                      note={note}
                      edit={() => {
                        setEditNote(note);
                        setMode('edit');
                      }}
                      deleteNote={() => deleteNoteItem(note)}
                      onView={() => setViewNote(note)}
                    />
                  ))}
                </div>
              </>
            )}
          </Modal>
          {viewNote && (
            <Modal
              kind="generic"
              settings={{
                title: t('View Note'),
                handleClose: () => setViewNote(null),
              }}
              className="w-[600px] rounded-md bg-white"
            >
              <div className="flex flex-col gap-4 p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('Date')}</p>
                    <p className="mt-1 text-base">
                      {formatDate(new Date(viewNote.interactionDate))}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">{t('Title')}</p>
                    <p className="mt-1 text-lg font-medium">{viewNote.title}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500">{t('Category')}</p>
                    <div className="mt-1">
                      <span
                        className={`inline-flex rounded-md px-3 py-1 text-sm font-medium ${categoryColors[viewNote.category] ?? defaultCategoryStyle}`}
                      >
                        {viewNote.category.charAt(0).toUpperCase() +
                          viewNote.category.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('Description')}</p>
                  <div className="mt-1 rounded-md bg-gray-50 p-4">
                    <div dangerouslySetInnerHTML={{ __html: viewNote.content }} />
                  </div>
                </div>
                <div className="border-t pt-4 text-sm text-gray-500">
                  {`${t('general.createdBy')}: ${viewNote?.updateByName || ''} | 
                  ${t('general.createdAt')}: ${formatUnixTime(
                    viewNote.tcreate,
                    'dd/MM/yyyy'
                  )} | 
                  ${t('general.modifiedAt')}: ${formatUnixTime(
                    viewNote.tupdate || viewNote.tcreate,
                    'dd/MM/yyyy HH:mm a'
                  )}`}
                </div>

                <div className="mt-4 flex justify-end gap-3 border-t pt-4">
                  <Button
                    kind="outline"
                    onClick={() => {
                      setViewNote(null);
                      setEditNote(viewNote);
                      setMode('edit');
                    }}
                  >
                    {t('Edit')}
                  </Button>
                  <Button kind="outline_red" onClick={() => setViewNote(null)}>
                    {t('Close')}
                  </Button>
                </div>
              </div>
            </Modal>
          )}
        </>
      )}
    </>
  );
}

function NoteDetail({
  note,
  edit,
  deleteNote,
  onView,
}: {
  note: DTNote;
  edit: () => void;
  deleteNote: () => void;
  onView: () => void;
}) {
  const { t } = useTranslation();
  const items: LabelledItemShape[] = [
    { label: t('general.createdAt'), value: formatDate(new Date(note.tcreate)) },
    { label: t('general.createdBy'), value: note.updateByName || '' },
    { label: t('general.category'), value: titleCase(note.category) },
  ];
  return (
    <div className="relative flex flex-col gap-1 rounded-lg border border-gray-200 p-4 hover:border-gray-400">
      <div className="absolute top-2 right-2 flex gap-2">
        <Button kind="primary" size="sm" onClick={() => onView()}>
          <EyeIcon className="h-4 w-4 text-white" />
        </Button>
        <Button kind="primary" size="sm" onClick={() => edit()}>
          <PencilIcon className="h-4 w-4 text-white" />
        </Button>
        <Button kind="red" size="sm" onClick={() => deleteNote()}>
          <TrashIcon className="h-4 w-4 text-white" />
        </Button>
      </div>
      <div className="font-nunito text-large font-bold">{note.title}</div>
      <div
        className="font-nunito text-base text-gray-600 line-clamp-2"
        dangerouslySetInnerHTML={{ 
          __html: note.content
        }}
      />
      <LabelledItemList
        items={items}
        listWrapperClasses="mt-2 grid grid-cols-3 gap-4"
        itemProps={{ labelClasses: 'text-sm text-gray-500 font-nunito' }}
      />
    </div>
  );
}

export default Notes;
